import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Bot, Map, Code, CreditCard, Shield, Settings,
  BookOpen, Video, FileQuestion, Mail, ArrowLeft, ExternalLink
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Language } from '@/types';

interface HelpCenterProps {
  lang: Language;
}

const HelpCenter: React.FC<HelpCenterProps> = ({ lang }) => {
  const navigate = useNavigate();
  const isRtl = lang === 'ar';

  const appLoaded = typeof window !== 'undefined' && sessionStorage.getItem('appLoaded') === 'true';
  
  const helpCategories = [
    {
      icon: <Bot size={22} />,
      title: lang === 'ar' ? 'البدء مع VEXL Nova' : 'Getting Started with VEXL Nova',
      description: lang === 'ar' ? 'تعلم أساسيات استخدام مساعد الذكاء المكاني' : 'Learn the fundamentals of using the Spatial Intelligence Assistant',
      articles: [
        { title: lang === 'ar' ? 'ما هو VEXL Nova؟' : 'What is VEXL Nova?', id: 'what-is-nova' },
        { title: lang === 'ar' ? 'كيف أبدأ جلسة عمل جديدة؟' : 'How to start a new session?', id: 'new-session' },
        { title: lang === 'ar' ? 'أنواع الاستعلامات المكانية المدعومة' : 'Supported spatial query primitives', id: 'query-types' },
        { title: lang === 'ar' ? 'فهم تحليلات الخرائط والأكواد' : 'Interpreting map visualizers and scripts', id: 'interpret-results' },
      ],
    },
    {
      icon: <Map size={22} />,
      title: lang === 'ar' ? 'تحليل البيانات الجيومكانية' : 'Spatial Data Analysis',
      description: lang === 'ar' ? 'معالجة وتحليل ملفات GIS و GeoParquet' : 'Ingest and compute on GIS, GeoParquet & FlatGeoBuf layers',
      articles: [
        { title: lang === 'ar' ? 'الصيغ المكانية المدعومة' : 'Supported spatial formats', id: 'file-formats' },
        { title: lang === 'ar' ? 'تحليل مضلعات Shapefiles' : 'Ingesting & analyzing Shapefiles', id: 'shapefiles' },
        { title: lang === 'ar' ? 'التعامل مع ملفات GeoParquet' : 'Working with GeoParquet datasets', id: 'geoparquet-data' },
        { title: lang === 'ar' ? 'تصدير الخرائط والمؤشرات' : 'Exporting indices and layers', id: 'export' },
      ],
    },
    {
      icon: <Code size={22} />,
      title: lang === 'ar' ? 'ربط ArcGIS Pro و QGIS' : 'Desktop GIS Integration',
      description: lang === 'ar' ? 'توصيل VEXL بأدوات سطح المكتب الجغرافية' : 'Connect VEXL runtimes to desktop GIS work environments',
      articles: [
        { title: lang === 'ar' ? 'تحميل أداة VEXL Toolbox' : 'Downloading VEXL Desktop Toolbox', id: 'download-toolbox' },
        { title: lang === 'ar' ? 'إنشاء مفتاح الترخيص والمزامنة' : 'Generating access tokens', id: 'access-token' },
        { title: lang === 'ar' ? 'تثبيت الأداة في ArcGIS Pro' : 'Installing in ArcGIS Pro', id: 'install-arcgis' },
        { title: lang === 'ar' ? 'استكشاف أخطاء الربط وإصلاحها' : 'Troubleshooting connection pipelines', id: 'troubleshoot' },
      ],
    },
    {
      icon: <CreditCard size={22} />,
      title: lang === 'ar' ? 'الخطط والمؤسسات' : 'Plans & Enterprise Access',
      description: lang === 'ar' ? 'إدارة التراخيص ومستويات الاستهلاك' : 'Manage computational compute quotas and licensing',
      articles: [
        { title: lang === 'ar' ? 'مقارنة خطط الاستخدام' : 'Comparing execution tiers', id: 'compare-plans' },
        { title: lang === 'ar' ? 'الترقية إلى اشتراك Enterprise' : 'Upgrading to Enterprise', id: 'upgrade-enterprise' },
        { title: lang === 'ar' ? 'حدود الاستعلام السحابي اليومية' : 'Daily query concurrency limits', id: 'usage-limits' },
        { title: lang === 'ar' ? 'الفوترة وإدارة العقود' : 'Billing & licensing contracts', id: 'billing' },
      ],
    },
    {
      icon: <Shield size={22} />,
      title: lang === 'ar' ? 'الخصوصية والأمان المؤسسي' : 'Privacy & Security Protocols',
      description: lang === 'ar' ? 'معايير حماية البيانات والسيادة الرقمية' : 'How we safeguard sensitive telemetry and client boundaries',
      articles: [
        { title: lang === 'ar' ? 'سياسة حماية البيانات المكانية' : 'Spatial data governance policy', id: 'privacy' },
        { title: lang === 'ar' ? 'تشفير البيانات أثناء النقل والتخزين' : 'Data encryption protocols', id: 'encryption' },
        { title: lang === 'ar' ? 'وضع العمل المنعزل (Air-gapped)' : 'Air-gapped deployment architecture', id: 'air-gapped' },
        { title: lang === 'ar' ? 'حذف السجلات ومحو البيانات' : 'Telemetry purging and audit trails', id: 'deletion' },
      ],
    },
    {
      icon: <Settings size={22} />,
      title: lang === 'ar' ? 'إعدادات المنصة وبيئة العمل' : 'Workspace & Platform Settings',
      description: lang === 'ar' ? 'تخصيص لوحة القيادة ومفاتيح التشغيل' : 'Customize runtime configurations and spatial parameters',
      articles: [
        { title: lang === 'ar' ? 'إدارة مفاتيح API ومسارات الاتصال' : 'API tokens and webhook endpoints', id: 'api-tokens' },
        { title: lang === 'ar' ? 'اختصارات لوحة المفاتيح المتقدمة' : 'Pro spatial keyboard shortcuts', id: 'shortcuts' },
        { title: lang === 'ar' ? 'إدارة الجلسات والأرشيف المكاني' : 'Managing projects and layer cache', id: 'projects' },
        { title: lang === 'ar' ? 'تفضيلات الإسقاط واللغة' : 'CRS projections & language preferences', id: 'projections' },
      ],
    },
  ];

  const quickLinks = [
    { icon: <BookOpen size={15} />, label: lang === 'ar' ? 'دليل البدء السريع' : 'Quick Start Guide', href: '/docs' },
    { icon: <Code size={15} />, label: lang === 'ar' ? 'توثيق واجهة API' : 'API Documentation', href: '/developers' },
    { icon: <FileQuestion size={15} />, label: lang === 'ar' ? 'الأسئلة الشائعة' : 'Knowledge Base FAQ', href: '#faq' },
    { icon: <Mail size={15} />, label: lang === 'ar' ? 'فريق الدعم الفني' : 'Technical Support', href: 'mailto:vexl.gis@gmail.com' },
  ];

  return (
    <PageTransition>
      <SEO 
        title={lang === 'ar' ? 'مركز التوثيق والمساعدة | VEXL' : 'Documentation & Support | VEXL'}
        description="Official VEXL documentation, user guides, API specifications, and technical support portal."
        lang={lang}
      />
      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden ${isRtl ? 'font-arabic text-right' : 'font-sans text-left'}`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-28 pb-24">
          
          {/* Back Button */}
          <motion.button
            initial={appLoaded ? false : { opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-xs font-mono tracking-widest uppercase mb-10"
          >
            <ArrowLeft size={14} className={isRtl ? 'rotate-180' : ''} />
            <span>{lang === 'ar' ? 'العودة للخلف' : 'Back'}</span>
          </motion.button>

          {/* Two-Column Editorial Split Hero */}
          <div className="pb-16 border-b border-[#262C31] mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
              
              {/* Left Column: Headline */}
              <div className="lg:col-span-7">
                <h1 
                  className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {lang === 'ar' ? 'مركز التوثيق والدعم التقني' : 'Documentation & Technical Support'}
                </h1>
              </div>

              {/* Right Column: Editorial Description & Live Indicator */}
              <div className="lg:col-span-5 space-y-4">
                <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                  {lang === 'ar'
                    ? 'ابحث في الوثائق الإرشادية والأدلة المعرفية لحلول الذكاء الاصطناعي الجغرافي، معايير التكامل، ومحرك VEXL Nova.'
                    : 'Explore comprehensive technical guides, API references, and architecture standards for VEXL Nova and the spatial intelligence ecosystem.'}
                </p>
                <div className="pt-2">
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[#0E93A3] uppercase tracking-[0.18em]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] animate-pulse" />
                    {lang === 'ar' ? 'نظام الدعم الفني المباشر' : 'Support Knowledge Base · Online'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links Strip */}
          <motion.div
            initial={appLoaded ? false : { opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center gap-3 mb-16"
          >
            {quickLinks.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#12161A] border border-[#262C31] text-xs font-mono text-[#9AA5AC] hover:text-white hover:border-[#0E93A3]/50 transition-all duration-200"
              >
                <span className="text-[#0E93A3]">{link.icon}</span>
                <span>{link.label}</span>
                {link.href.startsWith('mailto') && <ExternalLink size={11} className="opacity-50" />}
              </a>
            ))}
          </motion.div>

          {/* Categories Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {helpCategories.map((category, idx) => (
              <motion.div
                key={idx}
                initial={appLoaded ? false : { opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
                className="p-6 md:p-8 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-lg bg-[#171C21] border border-[#262C31] flex items-center justify-center text-[#0E93A3] mb-5 group-hover:bg-[#0E93A3]/10 transition-colors">
                    {category.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-[#EDF0F1] mb-2">
                    {category.title}
                  </h3>
                  <p className="text-[#9AA5AC] text-sm leading-relaxed mb-6 font-normal">
                    {category.description}
                  </p>
                </div>

                <ul className="space-y-2.5 pt-4 border-t border-[#262C31]">
                  {category.articles.map((article, aIdx) => (
                    <li key={aIdx}>
                      <button
                        type="button"
                        onClick={() => {}}
                        className="text-xs font-mono text-[#9AA5AC] hover:text-[#0E93A3] transition-colors flex items-center gap-2 group/item text-left w-full"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#262C31] group-hover/item:bg-[#0E93A3] transition-colors" />
                        <span className="truncate">{article.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Support Uplink Banner */}
          <div className="p-8 md:p-12 rounded-xl bg-[#12161A] border border-[#262C31] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-xl">
              <h3 className="text-xl md:text-2xl font-semibold text-[#EDF0F1]">
                {lang === 'ar' ? 'هل تحتاج إلى استشارة معمارية مخصصة؟' : 'Need enterprise architectural support?'}
              </h3>
              <p className="text-[#9AA5AC] text-sm leading-relaxed font-normal">
                {lang === 'ar' 
                  ? 'فريق هندسة النظم المكانية متاح لدعمكم في ربط خطوط الإنتاج والبيانات الحساسة.'
                  : 'Our GeoAI engineering team is available for custom deployments, security audits, and dedicated SLAs.'}
              </p>
            </div>
            <a
              href="mailto:vexl.gis@gmail.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0E93A3] text-white font-medium text-xs font-mono tracking-wider uppercase hover:bg-[#075C68] transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)] shrink-0"
            >
              <Mail size={14} />
              <span>{lang === 'ar' ? 'تواصل مع المهندسين' : 'Open Support Ticket'}</span>
            </a>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default HelpCenter;
